import { useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Tooltip,
  Switch,
  FormControlLabel,
  alpha,
} from "@mui/material";
import {
  ExportOptionsRoot,
  ExportOptionsBlock,
  ExportOptionsStack,
  ExportOptionsChip,
} from "./ExportOptions.styles";
import { data } from "./data";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

// Export

export const ExportOptions = (props) => {
  const { clickOnAction, onStartExport } = props;

  const {
    currentAspectRatio: selectedAR, // Read current AR state
    updateAspectRatio: setSelectedAR, // Use context setter for AR
    currentResolution: selectedResolution, // Read current Resolution state
    updateResolution: setSelectedResolution, // Use context setter for Resolution
    showDynamicViewportOverlay,
    toggleShowDynamicViewportOverlay,
  } = useSelectedOptions();

  const theme = useTheme();

  // Map AR → allowed resolutions
  const RES_BY_AR = {
    "1:1": ["1080 x 1080", "2160 x 2160"],
    "4:5": ["1080 x 1350", "2160 x 2700"],
    "9:16": ["1080 x 1920", "2160 x 3840"],
    "16:9": ["1920 x 1080", "3840 x 2160"],
  };

  // Local state
  const allowedResList = useMemo(
    // selectedAR is now from context
    () => new Set(RES_BY_AR[selectedAR] ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAR]
  );

  // Helpers
  const isResolutionDisabled = (resName) => {
    // Only enable resolutions that match the selected AR
    return !allowedResList.has(resName);
  };

  const handleSelectAR = (item) => {
    const nextAR = item?.name;
    if (!nextAR) return;

    setSelectedAR(nextAR); // ✅ Use context setter

    // Reset resolution to the first allowed for the new AR
    const nextAllowed = RES_BY_AR[nextAR]?.[0] ?? null;
    const formattedNextAllowed = nextAllowed?.replaceAll(" ", "");

    setSelectedResolution(nextAllowed); // ✅ Use context setter

    const nextResDescriptor = { MeXRe: formattedNextAllowed };
    clickOnAction?.(nextResDescriptor); // send AR descriptor upstream
  };

  const handleSelectResolution = (item) => {
    const res = item?.name;
    if (!res) return;
    if (isResolutionDisabled(res)) return;

    setSelectedResolution(res); // ✅ Use context setter
    clickOnAction?.(item.action.descriptor); // send resolution descriptor upstream
  };

  const handleExport = (item) => {
    onStartExport?.();
    clickOnAction?.(item?.action?.descriptor);
  };

  return (
    <ExportOptionsRoot>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
        }}
      >
        <FormControlLabel
          labelPlacement="start"
          sx={{ margin: 0 }}
          control={
            <Switch
              checked={showDynamicViewportOverlay}
              onChange={(e) =>
                toggleShowDynamicViewportOverlay(e.target.checked)
              }
              name="previewSwitch"
              sx={{
                "& .MuiSwitch-track": {
                  backgroundColor: theme?.palette?.companyColors?.secondary,
                },
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: theme?.palette?.companyColors?.accent,
                  "&:hover": {
                    backgroundColor: alpha(
                      theme?.palette?.companyColors?.secondary,
                      theme?.palette?.companyColors?.opacity25
                    ),
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: alpha(
                    theme?.palette?.companyColors?.secondary,
                    theme?.palette?.companyColors?.opacity75
                  ),
                },
              }}
            />
          }
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, fontSize: "1.25rem" }}
            >
              Show viewport preview
            </Typography>
          }
        />
        {(data?.subActions ?? []).map((item, indexItem) => {
          const isAR = item?.name === "Aspect Ratio";
          const isResolution = item?.name === "Resolution";
          const isExport = item?.name === "Export image";

          return (
            <ExportOptionsBlock key={`${item?.name + indexItem}`}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  color: theme?.palette?.companyColors?.primary,
                  borderBottom: `1px solid ${theme?.palette?.companyColors?.secondary}`,
                  marginBottom: 2,
                }}
              >
                {item?.name}
              </Typography>
              {isExport && (
                <Tooltip
                  title="Export an image with the current view and your settings"
                  sx={{ fontSize: "2.5rem" }}
                >
                  <Button
                    startIcon={item?.subActions[0]?.muiIcon}
                    sx={{
                      color: theme?.palette?.companyColors?.secondary,
                      background: theme?.palette?.companyColors?.accent,
                      boxShadow: "0px 0px 5px rgba(0,0,0,0.2)",
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      borderRadius: theme?.borderRadius?.small,
                      padding: {
                        xs: "0.9rem 2rem",
                        lg: "1rem 1.5rem",
                      },
                    }}
                    onClick={() => handleExport(item?.subActions[0])}
                  >
                    <Typography
                      variant="button"
                      icon
                      sx={{
                        textTransform: "initial",
                        fontSize: {
                          xs: "1.75rem",
                          lg: "1.5rem",
                        },
                        fontWeight: 400,
                        width: "max-content",
                      }}
                    >
                      Export image
                    </Typography>
                  </Button>
                </Tooltip>
              )}
              {isAR && (
                <ExportOptionsStack direction="row" columns={4}>
                  {item.subActions.map((inner, i) => {
                    const val = inner?.action?.descriptor?.MeXAR;
                    const selected = val === selectedAR;
                    return (
                      <Tooltip title={inner?.tooltip}>
                        <ExportOptionsChip
                          key={inner.name + i}
                          label={inner.name}
                          icon={inner.muiIcon}
                          clickable
                          selected={selected}
                          variant="filled"
                          onClick={() => {
                            handleSelectAR(inner);
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </ExportOptionsStack>
              )}
              {isResolution && (
                <ExportOptionsStack direction="row" columns={3}>
                  {item.subActions.map((inner, i) => {
                    const resName = inner?.name; // e.g. "1080x1920"
                    const disabled = isResolutionDisabled(resName);
                    const selected = selectedResolution === resName;
                    return (
                      <ExportOptionsChip
                        key={inner.name + i}
                        label={inner.name}
                        clickable={!disabled}
                        disabled={disabled}
                        selected={selected}
                        variant="filled"
                        onClick={() => handleSelectResolution(inner)}
                      />
                    );
                  })}
                </ExportOptionsStack>
              )}
            </ExportOptionsBlock>
          );
        })}
      </Box>
    </ExportOptionsRoot>
  );
};
